import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, usePage } from "@inertiajs/react";
import { Badge, IconButton, Tooltip } from "@radix-ui/themes";
import classNames from "classnames";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  type LucideIcon,
} from "lucide-react";
import type { SharedProps } from "@/types";
import {
  SETTINGS_ITEM,
  isActive,
  navSections,
  type NavItem,
} from "@/shared/nav/navItems";
import { signOut } from "@/shared/nav/signOut";

interface Props {
  /** Icons only, labels in tooltips. */
  collapsed?: boolean;
  /** Shows the collapse/expand control on the right edge. Left off in the mobile drawer. */
  onToggle?: () => void;
}

// Fills its parent's height and scrolls itself, with the scrollbar hidden. A
// floating chevron at the bottom stands in for it while there's more below.
export default function AppSidebar({ collapsed = false, onToggle }: Props) {
  const { url, props } = usePage<SharedProps>();
  const path = url.split("?")[0];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
  }, []);

  // The window resizing, collapsing, and sections appearing (a new grant)
  // all change whether there's more below without a scroll event.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [measure]);

  const scrollDown = () => {
    const el = scrollRef.current;
    el?.scrollBy({ top: el.clientHeight * 0.75, behavior: "smooth" });
  };

  return (
    <div className="group/sidebar relative h-full">
      <div
        ref={scrollRef}
        onScroll={measure}
        className="h-full overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <SidebarNav collapsed={collapsed} path={path} props={props} />
      </div>

      {/* Layout classes go on plain wrappers, never on the Radix buttons:
          Radix Themes' CSS is unlayered, so it beats Tailwind's layered
          utilities (`.rt-BaseButton { display: inline-flex }` wins over
          `hidden`, its surface background over `bg-*`, and so on). */}
      {onToggle && (
        <div className="absolute -right-3 top-4 z-10 rounded-full bg-panel opacity-0 shadow-sm transition-opacity focus-within:opacity-100 group-hover/sidebar:opacity-100 [@media(hover:none)]:opacity-100">
          <Tooltip
            content={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            side="right"
          >
            <IconButton
              size="1"
              radius="full"
              variant="surface"
              color="gray"
              onClick={onToggle}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <ChevronRight size={14} />
              ) : (
                <ChevronLeft size={14} />
              )}
            </IconButton>
          </Tooltip>
        </div>
      )}

      {moreBelow && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-linear-to-t from-panel to-transparent pb-3 pt-8">
          <div className="pointer-events-auto rounded-full bg-panel shadow-md">
            <IconButton
              size="2"
              radius="full"
              variant="surface"
              color="gray"
              onClick={scrollDown}
              aria-label="Show more"
            >
              <ChevronDown size={16} />
            </IconButton>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarNav({
  collapsed,
  path,
  props,
}: {
  collapsed: boolean;
  path: string;
  props: SharedProps;
}) {
  return (
    <nav aria-label="Your Driftables" className="flex min-h-full flex-col p-3">
      {navSections(props).map((section, i) => (
        <div key={section.title ?? i} className="flex flex-col gap-0.5">
          {section.title &&
            (collapsed ? (
              <hr className="mx-2 my-3 border-gray6" />
            ) : (
              <p className="mb-1 mt-5 px-3 text-[11px] font-semibold uppercase tracking-wider text-gray11">
                {section.title}
              </p>
            ))}
          {section.items.map((item) => (
            <SidebarLink
              key={item.href}
              item={item}
              active={isActive(item, path)}
              count={item.count?.(props) ?? 0}
              collapsed={collapsed}
            />
          ))}
        </div>
      ))}

      <div className="mt-auto flex flex-col gap-0.5 border-t border-gray6 pt-3">
        <SidebarLink
          item={SETTINGS_ITEM}
          active={isActive(SETTINGS_ITEM, path)}
          count={0}
          collapsed={collapsed}
        />
        <SidebarButton
          icon={LogOut}
          label="Sign out"
          onClick={signOut}
          collapsed={collapsed}
        />
      </div>
    </nav>
  );
}

const rowClass = (collapsed: boolean, active = false) =>
  classNames(
    "flex min-h-10 w-full items-center gap-3 rounded-md text-sm no-underline transition-colors",
    collapsed ? "justify-center px-0" : "px-3",
    active
      ? "bg-accent3 font-medium text-accent11"
      : "text-gray11 hover:bg-gray3 hover:text-gray12",
  );

function SidebarLink({
  item,
  active,
  count,
  collapsed,
}: {
  item: NavItem;
  active: boolean;
  count: number;
  collapsed: boolean;
}) {
  const Icon = item.icon;
  return (
    <WithTooltip label={item.label} show={collapsed}>
      <Link
        href={item.href}
        className={classNames(rowClass(collapsed, active), "relative")}
        aria-current={active ? "page" : undefined}
        aria-label={collapsed ? item.label : undefined}
      >
        <Icon size={18} aria-hidden className="shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
        {count > 0 &&
          (collapsed ? (
            <span
              className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[var(--amber-9)]"
              aria-hidden
            />
          ) : (
            <Badge ml="auto" color="amber" radius="full">
              {count}
            </Badge>
          ))}
      </Link>
    </WithTooltip>
  );
}

function SidebarButton({
  icon: Icon,
  label,
  onClick,
  collapsed,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  collapsed: boolean;
}) {
  return (
    <WithTooltip label={label} show={collapsed}>
      <button
        type="button"
        onClick={onClick}
        className={classNames(rowClass(collapsed), "cursor-pointer")}
        aria-label={collapsed ? label : undefined}
      >
        <Icon size={18} aria-hidden className="shrink-0" />
        {!collapsed && <span>{label}</span>}
      </button>
    </WithTooltip>
  );
}

function WithTooltip({
  label,
  show,
  children,
}: {
  label: string;
  show: boolean;
  children: ReactNode;
}) {
  if (!show) return <>{children}</>;
  return (
    <Tooltip content={label} side="right">
      {children}
    </Tooltip>
  );
}
